import { useEffect, useRef } from 'react';
import { useGridApiContext, useGridRootProps } from '../lib/dataGrid';
import { readPrefs, writePrefs } from './gridPreferences';

/**
 * Restore this grid's saved layout on mount, then keep it saved as the user
 * changes it. Runs INSIDE the grid (it reads the api from context), so the
 * grids need no apiRef plumbing.
 *
 * Every grid must pass a DISTINCT id. An office grid and its Auswertung twin
 * share the columns hook but not the context, so they must not share a layout.
 *
 * `hiddenByDefault` names columns the grid ships hidden and that a layout saved
 * before they existed must not switch on (see readPrefs).
 */
export function useGridPreferences(
    gridId: string | undefined,
    hiddenByDefault?: readonly string[]
): void {
    const apiRef = useGridApiContext();
    // `restoreState` fires the very events we listen on; without this the
    // restore would write itself back, and a half-applied restore could be
    // persisted as the new truth.
    const restoring = useRef(false);
    // A joined string, not the array: a caller that spells the list inline would
    // otherwise hand a new array every render and re-run the whole effect.
    const hiddenFields = (hiddenByDefault ?? []).join(',');
    // Compared in the event — see restoreColumns. This child effect runs before
    // the grid's own, which is the one that rebuilds the columns.
    const { columns } = useGridRootProps();
    const columnsProp = useRef(columns);
    useEffect(() => {
        columnsProp.current = columns;
    }, [columns]);

    useEffect(() => {
        if (!gridId) {
            return;
        }
        const hidden = hiddenFields.split(',').filter(Boolean);
        const saved = readPrefs(gridId, hidden);
        // The stored column layout, kept at hand: the grid rebuilds its columns
        // on nearly every render (see restoreColumns), and re-reading storage
        // each time would parse every grid's layout each time.
        let layout = saved?.columns;
        if (saved) {
            restoring.current = true;
            apiRef.current.restoreState(saved);
            restoring.current = false;
        }

        /**
         * A NEW `columns` array makes the grid rebuild its columns from the
         * definitions, dropping order and widths — and the column hooks hand a
         * new array on every render. Put the stored layout back each time.
         * Only a changed prop counts: dragging a header fires `columnsChange`
         * too, and restoring then would snap the column back mid-drag.
         */
        let seenColumns = columnsProp.current;
        const restoreColumns = () => {
            if (restoring.current || columnsProp.current === seenColumns) {
                return;
            }
            seenColumns = columnsProp.current;
            if (!layout) {
                return;
            }
            restoring.current = true;
            apiRef.current.restoreState({ columns: layout });
            restoring.current = false;
        };

        const save = () => {
            if (restoring.current) {
                return;
            }
            // Export everything, keep the four parts worth keeping — see
            // gridPreferences for why filters are not among them.
            writePrefs(gridId, apiRef.current.exportState());
            layout = readPrefs(gridId, hidden)?.columns;
        };

        const unsubscribes = [
            apiRef.current.subscribeEvent('columnVisibilityModelChange', save),
            apiRef.current.subscribeEvent('sortModelChange', save),
            apiRef.current.subscribeEvent('columnOrderChange', save),
            apiRef.current.subscribeEvent('columnWidthChange', save),
            apiRef.current.subscribeEvent('columnsChange', restoreColumns)
        ];
        return () => {
            for (const unsubscribe of unsubscribes) {
                unsubscribe();
            }
        };
    }, [apiRef, gridId, hiddenFields]);
}
